'use client';

import { c as _c } from "react/compiler-runtime";
import clsx from 'clsx';
import { Link as RACLink } from 'react-aria-components';
import stylesAsButton from '~/components/Button/src/Button.module.css';
import { isMigratedPathname } from '~/i18n/i18n-config';
import { Link as IntlLink } from '~/i18n/navigation';
import styles from './Link.module.css';
import type { LinkProps } from './Link.types';
const isBywayInternalUrl = (href: LinkProps['href']) => {
  if (!href) return false;
  if (typeof href !== 'string') return false;
  if (href.startsWith('/')) return true;
  try {
    const url = new URL(href);
    return url.hostname.endsWith('byway.travel');
  } catch {
    return false;
  }
};
const resolveTarget = (href: LinkProps['href'], target: LinkProps['target'], explicitTarget: boolean): LinkProps['target'] => {
  if (target !== '_blank') {
    return target;
  }
  if (explicitTarget) {
    return target;
  }
  if (isBywayInternalUrl(href)) {
    return undefined;
  }
  return target;
};
const Link = t0 => {
  const $ = _c(53);
  let asButton;
  let children;
  let dataTestId;
  let hasUnderline;
  let href;
  let isDisabled;
  let isExternal;
  let isFullWidth;
  let isInline;
  let isPill;
  let isResponsive;
  let isTransparent;
  let isUnstyled;
  let noWrap;
  let onClick;
  let props;
  let size;
  let target;
  if ($[0] !== t0) {
    ({
      asButton,
      isPill,
      isTransparent,
      children,
      dataTestId,
      hasUnderline,
      href,
      isDisabled,
      isExternal,
      isFullWidth,
      isInline,
      isResponsive,
      isUnstyled,
      noWrap,
      onClick,
      size,
      target,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = asButton;
    $[2] = children;
    $[3] = dataTestId;
    $[4] = hasUnderline;
    $[5] = href;
    $[6] = isDisabled;
    $[7] = isExternal;
    $[8] = isFullWidth;
    $[9] = isInline;
    $[10] = isPill;
    $[11] = isResponsive;
    $[12] = isTransparent;
    $[13] = isUnstyled;
    $[14] = noWrap;
    $[15] = onClick;
    $[16] = props;
    $[17] = size;
    $[18] = target;
  } else {
    asButton = $[1];
    children = $[2];
    dataTestId = $[3];
    hasUnderline = $[4];
    href = $[5];
    isDisabled = $[6];
    isExternal = $[7];
    isFullWidth = $[8];
    isInline = $[9];
    isPill = $[10];
    isResponsive = $[11];
    isTransparent = $[12];
    isUnstyled = $[13];
    noWrap = $[14];
    onClick = $[15];
    props = $[16];
    size = $[17];
    target = $[18];
  }
  const defaultTarget = target ?? (isBywayInternalUrl(href) ? undefined : "_blank");
  const t1 = target !== undefined;
  let t2;
  if ($[19] !== defaultTarget || $[20] !== href || $[21] !== t1) {
    t2 = resolveTarget(href, defaultTarget, t1);
    $[19] = defaultTarget;
    $[20] = href;
    $[21] = t1;
    $[22] = t2;
  } else {
    t2 = $[22];
  }
  const resolvedTarget = t2;
  const rel = resolvedTarget === "_blank" ? "noreferrer noopener" : undefined;
  const isInternalNonMigratedHref = typeof href === "string" && href.startsWith("/") && !isMigratedPathname(href.split(/[?#]/)[0]);
  const InternalLink = isDisabled || isInternalNonMigratedHref ? "a" : IntlLink;
  let t3;
  if ($[23] !== asButton || $[24] !== hasUnderline || $[25] !== isDisabled || $[26] !== isExternal || $[27] !== isFullWidth || $[28] !== isInline || $[29] !== isPill || $[30] !== isResponsive || $[31] !== isTransparent || $[32] !== isUnstyled || $[33] !== noWrap || $[34] !== size) {
    t3 = isUnstyled ? undefined : clsx(styles.link, {
      [styles.inline]: isInline,
      [styles.underline]: hasUnderline,
      [styles.external]: isExternal,
      [styles.disabled]: isDisabled,
      [stylesAsButton.button]: asButton,
      [stylesAsButton.primary]: asButton === "primary",
      [stylesAsButton.secondary]: asButton === "secondary",
      [stylesAsButton.tertiary]: asButton === "tertiary",
      [stylesAsButton.neutral]: asButton === "neutral",
      [stylesAsButton.pill]: isPill,
      [stylesAsButton.transparent]: isTransparent,
      [stylesAsButton.icon]: asButton === "icon",
      [stylesAsButton.small]: size === "small",
      [stylesAsButton.extraSmall]: size === "extraSmall",
      [stylesAsButton.fullWidth]: size === "fullWidth" || isFullWidth,
      [stylesAsButton.responsive]: size === "responsive" || isResponsive,
      [stylesAsButton.large]: size === "large",
      [stylesAsButton.noWrap]: noWrap
    });
    $[23] = asButton;
    $[24] = hasUnderline;
    $[25] = isDisabled;
    $[26] = isExternal;
    $[27] = isFullWidth;
    $[28] = isInline;
    $[29] = isPill;
    $[30] = isResponsive;
    $[31] = isTransparent;
    $[32] = isUnstyled;
    $[33] = noWrap;
    $[34] = size;
    $[35] = t3;
  } else {
    t3 = $[35];
  }
  const linkStyles = t3;
  let t4;
  if ($[36] !== isDisabled || $[37] !== onClick) {
    t4 = e => {
      if (isDisabled) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      onClick?.(e);
    };
    $[36] = isDisabled;
    $[37] = onClick;
    $[38] = t4;
  } else {
    t4 = $[38];
  }
  const handleClick = t4;
  let t5;
  if ($[39] !== InternalLink || $[40] !== children || $[41] !== dataTestId || $[42] !== handleClick || $[43] !== href || $[44] !== isDisabled || $[45] !== isExternal || $[46] !== isUnstyled || $[47] !== linkStyles || $[48] !== onClick || $[49] !== props || $[50] !== rel || $[51] !== resolvedTarget) {
    t5 = <>{isExternal ? <RACLink {...props} data-test-id={dataTestId ?? "Link-external"} href={isDisabled ? undefined : href} target={resolvedTarget} rel={rel} className={linkStyles} isDisabled={isDisabled} onClick={e_0 => {
        if (!isDisabled) {
          onClick?.(e_0);
        }
      }}>{children}</RACLink> : <InternalLink {...props} data-test-id={dataTestId ?? "Link-internal"} href={isDisabled ? undefined : href} target={resolvedTarget} rel={rel} className={linkStyles} onClick={handleClick} aria-disabled={isDisabled}>{isUnstyled ? children : <span className={styles.span}>{children}</span>}</InternalLink>}</>;
    $[39] = InternalLink;
    $[40] = children;
    $[41] = dataTestId;
    $[42] = handleClick;
    $[43] = href;
    $[44] = isDisabled;
    $[45] = isExternal;
    $[46] = isUnstyled;
    $[47] = linkStyles;
    $[48] = onClick;
    $[49] = props;
    $[50] = rel;
    $[51] = resolvedTarget;
    $[52] = t5;
  } else {
    t5 = $[52];
  }
  return t5;
};
export { Link };